import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Award, Gift, TrendingUp, Heart, 
  History, Sparkles, Coins, Check, Flame, Brain, Users, Loader2
} from "lucide-react";

export default function SMAHOOCoins() {
  const { user, updateUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [dailyRewards, setDailyRewards] = useState(null);
  const [donateAmount, setDonateAmount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await apiClient.get('/coins/balance');
      setBalance(response.data.balance);
    } catch (error) {
      // silent
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await apiClient.get('/coins/history');
      setHistory(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  const fetchDailyStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/coins/daily-status');
      setDailyRewards(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchHistory();
    fetchDailyStatus();
  }, [fetchBalance, fetchHistory, fetchDailyStatus]);

  const claimDaily = async () => {
    setClaiming("daily_login");
    try {
      const res = await apiClient.post('/coins/claim-daily');
      setBalance(res.data.new_balance);
      updateUser({ smahoo_coins: res.data.new_balance });
      fetchDailyStatus();
      fetchHistory();
    } catch {
      // already claimed
    } finally {
      setClaiming(null);
    }
  };

  const claimStreak = async () => {
    setClaiming("weekly_streak");
    try {
      const res = await apiClient.post('/coins/claim-streak');
      setBalance(res.data.new_balance);
      updateUser({ smahoo_coins: res.data.new_balance });
      fetchDailyStatus();
      fetchHistory();
    } catch {
      // not eligible
    } finally {
      setClaiming(null);
    }
  };

  const handlePayItForward = async () => {
    if (donateAmount <= 0 || donateAmount > balance) return;
    
    setIsLoading(true);
    try {
      await apiClient.post('/coins/pay-it-forward', { amount: donateAmount });
      fetchBalance();
      fetchHistory();
      updateUser({ smahoo_coins: balance - donateAmount });
    } catch (error) {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyCoins = async (packageId) => {
    try {
      const response = await apiClient.post('/payments/checkout/session', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      // silent
    }
  };

  const COIN_PACKAGES = [
    { id: "coins-100", amount: 100, price: 9.99, popular: false },
    { id: "coins-500", amount: 500, price: 39.99, popular: true },
    { id: "coins-1000", amount: 1000, price: 69.99, popular: false },
  ];

  const DONATE_AMOUNTS = [10, 25, 50, 100];

  const rewardIcons = { daily_login: Award, mood_checkin: Heart, community_post: Users, ai_coaching: Brain };
  const rewardLinks = { mood_checkin: "/mood", community_post: "/community", ai_coaching: "/ai-coach" };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="smahoo-coins-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">RAP.Loyalty Rewards</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            SMAHOO Coins
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Earn coins through your daily wellness activities and use them in the Super Store, 
            for premium features, sessions, or pay-it-forward to support community wellness.
          </p>
        </div>

        {/* Balance Card */}
        <div className="spa-card bg-gradient-to-br from-spa-terracotta to-spa-terracotta-dark text-white relative overflow-hidden" data-testid="balance-card">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Coins className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-white/80 text-sm">Your Balance</p>
                <p className="font-serif text-4xl coins-glow">{balance.toFixed(0)}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-white/70 text-sm">SMAHOO Coins</p>
              {dailyRewards?.streak > 0 && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-white/15 rounded-full">
                  <Flame className="w-3.5 h-3.5 text-orange-300" />
                  <span className="text-white/90 text-xs font-medium">{dailyRewards.streak} day streak</span>
                </div>
              )}
            </div>
          </div>
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute -right-5 -bottom-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Ways to Earn — Interactive */}
          <div className="lg:col-span-2 space-y-6">
            {/* Daily Rewards */}
            <div className="spa-card" data-testid="daily-rewards-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-spa-moss">Today's Rewards</h2>
                {dailyRewards && (
                  <span className="text-xs text-spa-text-secondary">
                    {dailyRewards.claimed.length}/{dailyRewards.rewards.length} claimed
                  </span>
                )}
              </div>

              {/* Progress bar */}
              {dailyRewards && (
                <div className="mb-6">
                  <div className="h-2 bg-spa-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500" 
                      style={{ width: `${(dailyRewards.claimed.length / dailyRewards.rewards.length) * 100}%` }}
                      data-testid="daily-progress"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {dailyRewards?.rewards.map((r) => {
                  const Icon = rewardIcons[r.type] || Award;
                  const link = rewardLinks[r.type];
                  return (
                    <div key={r.type} className={`flex items-center justify-between p-4 rounded-xl transition-all ${r.claimed ? "bg-spa-sage/10 border border-spa-sage/20" : "bg-spa-muted"}`} data-testid={`daily-reward-${r.type}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${r.claimed ? "bg-spa-sage/20" : "bg-white"}`}>
                          {r.claimed ? <Check className="w-5 h-5 text-spa-sage" /> : <Icon className="w-5 h-5 text-spa-terracotta" strokeWidth={1.5} />}
                        </div>
                        <div>
                          <span className={`font-medium ${r.claimed ? "text-spa-sage" : "text-spa-moss"}`}>{r.label}</span>
                          <p className="text-xs text-spa-text-secondary">{r.claimed ? "Completed today" : r.type === "daily_login" ? "Claim your daily check-in" : `Complete a ${r.label.toLowerCase()}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-medium ${r.claimed ? "text-spa-sage" : "text-amber-600"}`}>
                          {r.claimed ? <Check className="w-4 h-4" /> : `+${r.coins}`}
                        </span>
                        {!r.claimed && r.type === "daily_login" && (
                          <button onClick={claimDaily} disabled={claiming === "daily_login"} className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50" data-testid="claim-daily-coins">
                            {claiming === "daily_login" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim"}
                          </button>
                        )}
                        {!r.claimed && link && (
                          <a href={link} className="px-4 py-1.5 border border-spa-border text-spa-moss rounded-full text-sm font-medium hover:bg-spa-muted transition-colors">
                            Go
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Weekly Streak Bonus */}
                {dailyRewards?.streak_bonus && (
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 border-dashed ${dailyRewards.streak_bonus.eligible ? "border-orange-300 bg-orange-50/50" : "border-spa-border/50 bg-spa-muted/50"}`} data-testid="streak-bonus-row">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dailyRewards.streak_bonus.eligible ? "bg-orange-100" : "bg-white"}`}>
                        <Flame className={`w-5 h-5 ${dailyRewards.streak_bonus.eligible ? "text-orange-500" : "text-spa-border"}`} />
                      </div>
                      <div>
                        <span className="font-medium text-spa-moss">Weekly Streak Bonus</span>
                        <p className="text-xs text-spa-text-secondary">
                          {dailyRewards.streak_bonus.eligible 
                            ? `${dailyRewards.streak_bonus.current_streak} day streak — claim your bonus!` 
                            : `${dailyRewards.streak_bonus.current_streak}/7 days — keep going!`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-amber-600">+{dailyRewards.streak_bonus.coins}</span>
                      {dailyRewards.streak_bonus.eligible && (
                        <button onClick={claimStreak} disabled={claiming === "weekly_streak"} className="px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50" data-testid="claim-streak-coins">
                          {claiming === "weekly_streak" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Buy Coins */}
            <div className="spa-card" data-testid="buy-coins-card">
              <h2 className="font-serif text-xl text-spa-moss mb-6">Get More Coins</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {COIN_PACKAGES.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className={`relative p-6 rounded-2xl border-2 transition-all ${
                      pkg.popular 
                        ? 'border-spa-terracotta bg-spa-terracotta/5' 
                        : 'border-spa-border hover:border-spa-sage'
                    }`}
                  >
                    {pkg.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-spa-terracotta text-white text-xs px-3 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                    <div className="text-center">
                      <p className="font-serif text-3xl text-spa-moss mb-1">{pkg.amount}</p>
                      <p className="text-sm text-spa-text-secondary mb-4">coins</p>
                      <p className="text-2xl font-medium text-spa-moss mb-4">CA${pkg.price}</p>
                      <Button
                        onClick={() => handleBuyCoins(pkg.id)}
                        className={pkg.popular ? 'spa-button-primary w-full' : 'spa-button-secondary w-full'}
                        data-testid={`buy-${pkg.id}`}
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pay It Forward */}
            <div className="spa-card bg-spa-sage/10" data-testid="pay-forward-card">
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-6 h-6 text-spa-sage" />
                <h2 className="font-serif text-xl text-spa-moss">Pay It Forward</h2>
              </div>
              <p className="text-sm text-spa-text-secondary mb-4">
                Donate coins to help others in the community access wellness resources.
              </p>
              <div className="flex gap-2 mb-4">
                {DONATE_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setDonateAmount(amount)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                      donateAmount === amount
                        ? 'bg-spa-sage text-white'
                        : 'bg-white text-spa-text-secondary hover:bg-spa-muted'
                    }`}
                  >
                    {amount}
                  </button>
                ))}
              </div>
              <Button
                onClick={handlePayItForward}
                disabled={isLoading || donateAmount > balance}
                className="w-full spa-button-primary"
                data-testid="pay-forward-button"
              >
                <Heart className="w-4 h-4 mr-2" />
                Donate {donateAmount} Coins
              </Button>
            </div>

            {/* Transaction History */}
            <div className="spa-card" data-testid="history-card">
              <div className="flex items-center gap-3 mb-4">
                <History className="w-5 h-5 text-spa-text-secondary" />
                <h2 className="font-serif text-lg text-spa-moss">Recent Activity</h2>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {history.slice(0, 10).map((tx) => (
                  <div key={tx.transaction_id} className="flex items-center justify-between py-2 border-b border-spa-border last:border-0">
                    <div>
                      <p className="text-sm text-spa-moss">{tx.description || tx.type}</p>
                      <p className="text-xs text-spa-text-secondary">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`font-medium ${tx.amount > 0 ? 'text-spa-sage' : 'text-spa-terracotta'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </span>
                  </div>
                ))}
                {history.length === 0 && (
                  <p className="text-sm text-spa-text-secondary text-center py-4">
                    No transactions yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
